/* eslint-disable import/no-extraneous-dependencies */
'use client';
import { CustomEventName, GTMEventKey, sendGTM } from '@fe-cnyes/fe-common-gtm';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import { postTime } from '@fe-common-utils/libs/time';
import { styled } from '@linaria/react';
import cx from 'classnames';
import HTMLEllipsis from 'react-lines-ellipsis/lib/html';

const HAO_DEFAULT_IMAGE = '/assets/icons/hao/avatar-placeholder.svg';
const HAO_IMAGE_API_URL = '/download_image.ashx?width=92&height=92&mode=crop&file=';
const anueHaoLink = 'https://hao.cnyes.com';
const NEWLINE_REGEX = /\r\n|\n\n|\r|\n/g;
const TAG_REGEX = /[&<>]/g;

export interface HaoPostProps {
  item: {
    文章Id: number;
    文章型態: string;
    標題: string;
    摘要: string;
    貼文插件型態: string;
    長文發布時間: string;
    _號: any;
    CreatedAt: string;
  };
  source?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 389px;
  min-width: 389px;
  height: 156px;
  padding: 12px 16px 16px;
  border-radius: 6px;
  border: solid 1px #e2e8f1;
  background-color: white;

  a {
    text-decoration: none;
  }

  .hao-read-more {
    color: #54b5c6;
    cursor: pointer;
  }

  @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
    min-width: 100%;
    width: 100%;
  }
`;

const HaoUserContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;
`;

/**
 * 生成鉅亨號需要的 UTM 連結字串
 * 為了可分享鉅亨網導流至鉅亨號的數據
 *
 * @param {string} campaign UTM campaign 參數
 * @param {string} medium UTM medium 參數
 * @param {string} source UTM source 參數
 */
export const generateHaoLinkWithUtm = (link = '') => {
  const haoLink = !link.length ? anueHaoLink : link;

  return (campaign: any, medium = 'home', source = 'cnyes') => {
    return `${haoLink}?utm_source=${source}&utm_medium=${medium}&utm_campaign=${campaign}`;
  };
};

const handleErrImg = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  e.currentTarget.src = HAO_DEFAULT_IMAGE;
};

/**
 * 鉅亨號認證標章
 * 1 -> 沒有勾勾
 * 2 -> 黃勾勾
 * 3 -> 藍勾勾
 */
const haoAuthenticationIcons = {
  0: null,
  1: null,
  2: 'yellow-hook',
  3: 'blue-hook'
};

const HaoImage = styled.a`
  position: relative;
  display: flex;
  width: 38px;
  height: 38px;
  margin-right: 12px;

  a > * {
    pointer-events: none;
  }

  img {
    z-index: 1;
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
  }

  .badge {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 10px;
    height: 10px;
    display: inline-block;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    z-index: 1;

    &.blue-hook {
      background-image: url('/assets/icons/hao/hao-blue-hook.svg');
    }

    &.yellow-hook {
      background-image: url('/assets/icons/hao/hao-yellow-hook.svg');
    }

    span {
      display: none;
    }
  }
`;

const HaoName = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: space-evenly;
  height: 42px;
  min-height: 42px;

  h4 {
    color: #383838;
    font-size: 16px;
    line-height: 1.5;
    font-weight: 500;
    letter-spacing: 0.6px;
  }

  .time {
    color: #777777;
    font-size: 12px;
    line-height: 1.5;
    letter-spacing: 0.6px;
  }
`;

/**
 * 如果內容是貼文型態的話，對應取回的摘要內容為空時
 * 因應以下狀態，顯示對應預設內容
 *
 * "貼文插件型態":"video",
 * "貼文插件型態":"pictures",
 * "貼文插件型態":"links",
 *
 */
function getDefaultEmptyContentOfSummary(summary: any, plugin: any) {
  if (!summary || summary.length === 0) {
    switch (plugin) {
      case 'video':
        return '(影片分享，點擊前往觀看)';
      case 'pictures':
        return '(圖片分享，點擊前往觀看)';
      case 'links':
        return '(連結分享，點擊前往觀看)';
      default:
        return summary;
    }
  }

  return summary;
}

const ContentContainer = styled.a`
  color: #383838;
  line-height: 1.5;
  font-size: 16px;
  letter-spacing: 0.5px;
  word-break: break-word;
  width: 100%;
  overflow: hidden;
`;

const tagsToReplace = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;'
};

type TagsKey = keyof typeof tagsToReplace;

const replaceTag = (tag: string): string => {
  return tagsToReplace[tag as TagsKey] || tag;
};

export function HaoPost({ item, source = 'home' }: HaoPostProps) {
  const {
    文章Id: articleId, // 文章 ID
    文章型態: type, // 貼文|長文
    標題: title, // 長文用的內容
    摘要: summary, // 貼文用的內容
    貼文插件型態: plugin, // 貼文的型態
    長文發布時間: postedAt, // 長文發佈時間
    _號: article, // 號Id、大頭貼、名稱等資料
    CreatedAt: createdAt // 貼文發佈時間
  } = item;

  const userName = article ? article['名稱'] : '';
  const haoId = article ? +article['號Id'] : 0;
  const image = article ? article['大頭照組'] : null;
  const postUrl = haoId ? generateHaoLinkWithUtm(`${anueHaoLink}/post/${articleId}`)('postid', source) : '';
  const imgSrc = image ? `${anueHaoLink}${HAO_IMAGE_API_URL}${encodeURIComponent(image)}` : HAO_DEFAULT_IMAGE;
  const haoUrl = haoId ? generateHaoLinkWithUtm(`${anueHaoLink}/ch/${haoId}`)('haoid', source) : '';
  const authenticationId = article ? (article['號認證Ids'] as 0 | 1 | 2 | 3) : 0;
  const isLongArticle = type ? type === '長文' : false;
  const dateTime = isLongArticle ? postedAt : createdAt;
  const hookIcon = haoAuthenticationIcons[authenticationId];
  const content = isLongArticle ? title : getDefaultEmptyContentOfSummary(summary, plugin);
  const articlePostTime = postTime(new Date(dateTime).getTime());

  const handleClick = () => {
    sendGTM({
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TOPIC,
      [GTMEventKey.SECTION]: '%p_鉅亨號貼文',
      [GTMEventKey.CLICK_ITEM]: userName
    });
  };

  return (
    <Container>
      <HaoUserContainer>
        <HaoImage href={haoUrl}>
          <img src={imgSrc} alt={userName} onError={handleErrImg} />
          <div className={cx('badge', hookIcon)}>
            <span>官方認證</span>
          </div>
        </HaoImage>
        <HaoName>
          <a href={haoUrl} onClick={handleClick}>
            <h4>{userName}</h4>
          </a>
          {/* https://nextjs.org/docs/messages/react-hydration-error#solution-3-using-suppresshydrationwarning */}
          <a className='time' href={postUrl}>
            <time suppressHydrationWarning>{articlePostTime}</time>
          </a>
        </HaoName>
      </HaoUserContainer>
      <ContentContainer href={postUrl} onClick={handleClick}>
        <HTMLEllipsis
          unsafeHTML={`${content}`.replace(TAG_REGEX, replaceTag).replace(NEWLINE_REGEX, '<br />')}
          maxLine='3'
          ellipsisHTML='<span class="hao-read-more">&hellip;查看更多</span>'
          basedOn='letters'
        />
      </ContentContainer>
    </Container>
  );
}

export default HaoPost;
